"use client";

import { useEffect, useRef, useState } from "react";

// "We've shipped" stat bar — three counters that animate from 0 to their
// final value when the section scrolls into view. Adds a measurable
// "look what we've delivered" beat that the original okpromo.co.uk lacks.
// Numbers are placeholders until real client-confirmed counts arrive.

type Stat = {
  value: number;
  suffix: string;
  label: string;
};

const stats: Stat[] = [
  { value: 12, suffix: "+", label: "Clothing brands launched" },
  { value: 8, suffix: " wks", label: "Average launch timeline" },
  { value: 30, suffix: "+", label: "Shopify stores built" },
];

export function Stats() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="bg-bg-soft border-b border-border"
    >
      <div className="site-container py-10 lg:py-14">
        <ul className="grid gap-8 sm:grid-cols-3">
          {stats.map((s, i) => (
            <li key={s.label} className="text-center sm:text-left">
              <div className="flex items-baseline justify-center sm:justify-start gap-0.5">
                <CountUp target={s.value} visible={visible} delayMs={i * 120} />
                <span className="text-display text-accent leading-none" style={{ fontSize: "clamp(2rem,4vw + 1rem,3.25rem)" }}>
                  {s.suffix}
                </span>
              </div>
              <p className="mt-2 text-small uppercase tracking-wider text-text-muted">
                {s.label}
              </p>
            </li>
          ))}
        </ul>
        <p className="mt-6 text-small text-text-muted text-center sm:text-left">
          Numbers are placeholders. Real counts arrive with the next round of
          client input.
        </p>
      </div>
    </section>
  );
}

function CountUp({
  target,
  visible,
  durationMs = 1400,
  delayMs = 0,
}: {
  target: number;
  visible: boolean;
  durationMs?: number;
  delayMs?: number;
}) {
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!visible) return;
    let raf = 0;
    let start: number | null = null;
    const tick = (t: number) => {
      if (start === null) start = t;
      const elapsed = t - start - delayMs;
      if (elapsed < 0) {
        raf = requestAnimationFrame(tick);
        return;
      }
      const progress = Math.min(elapsed / durationMs, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setN(Math.round(target * eased));
      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visible, target, durationMs, delayMs]);

  return (
    <span
      className="text-display text-accent leading-none tabular-nums"
      style={{ fontSize: "clamp(2.5rem,5vw + 1rem,4rem)" }}
    >
      {n}
    </span>
  );
}
